import { type MenuItemProps, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import ModalityChip from '~/components/Chip/ModalityChip';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import ApprovalMark from '~/components/Structures/ApprovalMark';
import { AvailableStructureSetView } from '~/domain/projections/views';
import { useStructureSetDetailView } from '~/hooks/domain/useStructureSetDetailView';
import MenuChoice from '~/components/Menu/MenuChoice';
import { STATUS_MARK_PX } from '~/components/Menu/menuRowSizes';

/**
 * A structure set the picker menu offers, after its RTSS chip. A loaded set
 * shows its approval status and ROI count; a set known only from the session shows its name,
 * since neither is known before it is downloaded. The focus props a Menu
 * gives its direct children pass through to the row.
 */
export default function StructureSetOption({
    structureSet,
    selected,
    onClick,
    ...focusProps
}: Pick<MenuItemProps, 'autoFocus' | 'tabIndex'> & {
    structureSet: AvailableStructureSetView;
    selected: boolean;
    onClick: () => void;
}) {
    const detail = useStructureSetDetailView(structureSet.structureSetId);
    return (
        <MenuChoice
            {...focusProps}
            selected={selected}
            onClick={onClick}
            mark={
                // An empty column when nothing is known yet, so every name
                // in the list starts at the same place.
                detail ? (
                    <ApprovalMark
                        status={detail.approvalStatus}
                        size={STATUS_MARK_PX}
                    />
                ) : null
            }
            chip={<ModalityChip modality="RTSS" size="small" />}
            name={
                <SensitiveInfoTypography>
                    {structureSet.displayName}
                </SensitiveInfoTypography>
            }
            meta={
                detail && (
                    <FormattedMessage
                        id="datasetPicker.roiCount"
                        values={{
                            count: (
                                <Typography variant="monoMeta">
                                    {detail.numberOfStructures}
                                </Typography>
                            ),
                        }}
                    />
                )
            }
            data-testid={`structureset-menu-item-${structureSet.displayName}`}
        />
    );
}
